/**
 * Traducción entre el contrato del API y el modelo que ya usa la web.
 *
 * Existe para que un cambio de nombres en el backend no obligue a tocar ni un
 * componente: se ajusta aquí y ya está. También concentra las dos conversiones
 * que se repiten en todas las llamadas:
 *
 * - fechas ISO 8601 ↔ milisegundos desde epoch,
 * - imágenes: URL servida por el API ↔ base64 que produce el panel al subir un
 *   fichero con `FileReader`.
 */

import type { HeroButton, HeroSlide } from '../../data/heroSlides';
import type { News } from '../newsService';
import type { CrearSlideDto, HeroSlideDto, NoticiaDto, SubidaImagenDto } from './dto';

/* -------------------------------------------------------------------- fechas */

/** ISO 8601 → epoch en milisegundos. Una fecha ilegible cuenta como 0. */
export function isoAEpoch(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/** Epoch en milisegundos → ISO 8601 en UTC. */
export const epochAIso = (ms: number): string => new Date(ms).toISOString();

/* ------------------------------------------------------------------ imágenes */

/**
 * Parte un `data:` URL en las tres piezas que espera el API.
 *
 * El panel lee los ficheros con `FileReader.readAsDataURL`, así que lo que hay
 * en el formulario es `data:image/png;base64,iVBORw0…`. Si lo que llega ya es
 * una URL normal (una imagen que el API ya tenía), no hay nada que subir y se
 * devuelve `null`.
 */
export function aSubidaImagen(dataUrl: string, fileName: string): SubidaImagenDto | null {
  const coincidencia = /^data:([^;,]+);base64,(.*)$/s.exec(dataUrl);
  if (!coincidencia) return null;

  const [, contentType, base64] = coincidencia;
  if (!contentType || !base64) return null;

  return { fileName, contentType, base64 };
}

/* ------------------------------------------------------------------ noticias */

/**
 * Noticia del API → modelo `News` de la web. SOLO LECTURA: no hay
 * `noticiaHaciaDto` — esta web no manda noticias al backend, eso vive en el
 * CMS aparte (Web.CMS.React).
 */
export function noticiaDesdeDto(dto: NoticiaDto): News {
  return {
    id: dto.id,
    slug: dto.slug,
    title: dto.title,
    content: dto.content,
    // `imageData` se usa tal cual como `src`.
    imageData: dto.image?.url ?? '',
    imageFileName: dto.image?.fileName ?? '',
    excerpt: dto.excerpt,
    author: dto.author,
    // Esta web solo pide noticias publicadas (`listarNoticiasPublicadas`), así
    // que aquí siempre debería llegar `published`. Si el backend colara alguna
    // vez una pendiente o archivada, se trata igual que una publicada: no hay
    // "pending" en el modelo de una web sin panel de revisión.
    status: 'published',
    featured: dto.featured,
    ...(dto.externalUrl ? { externalUrl: dto.externalUrl } : {}),
    createdAt: isoAEpoch(dto.createdAt),
    ...(dto.publishedAt ? { publishedAt: isoAEpoch(dto.publishedAt) } : {}),
  };
}

/* -------------------------------------------------------------------- slides */

/** Slide del API → modelo `HeroSlide` del carrusel. */
export function slideDesdeDto(dto: HeroSlideDto): HeroSlide {
  return {
    id: dto.id,
    title: dto.title,
    subtitle: dto.subtitle,
    image: dto.image?.url ?? '',
    imageFileName: dto.image?.fileName ?? '',
    order: dto.order,
    status: dto.status,
    imageOffsetX: dto.imageOffsetX,
    imageOffsetY: dto.imageOffsetY,
    imageScale: dto.imageScale,
    buttons: dto.buttons.map(
      (boton): HeroButton => ({
        id: boton.id,
        label: boton.label,
        href: boton.href,
        variant: boton.variant,
      }),
    ),
    createdAt: isoAEpoch(dto.createdAt),
    updatedAt: isoAEpoch(dto.updatedAt),
  };
}

/** Datos del formulario → cuerpo del POST/PUT de slides. */
export function slideHaciaDto(datos: {
  title: string;
  subtitle: string;
  imageData: string;
  imageFileName: string;
  imageOffsetX: number;
  imageOffsetY: number;
  imageScale: number;
  buttons: HeroButton[];
  order?: number;
}): CrearSlideDto {
  return {
    title: datos.title,
    subtitle: datos.subtitle,
    image: aSubidaImagen(datos.imageData, datos.imageFileName),
    imageOffsetX: datos.imageOffsetX,
    imageOffsetY: datos.imageOffsetY,
    imageScale: datos.imageScale,
    // Sin `id`: los de los botones los asigna el backend al guardar.
    buttons: datos.buttons.map(({ label, href, variant }) => ({
      label,
      href,
      variant: variant ?? 'primary',
    })),
    ...(datos.order === undefined ? {} : { order: datos.order }),
  };
}
